import { useState } from 'react';
import { Loader2, FileText } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { CompletionConditionStatus } from '@/services/course.service';

interface Props {
  isOpen: boolean;
  condition: CompletionConditionStatus | null;
  accepting: boolean;
  onClose: () => void;
  onAccept: () => void;
}

/**
 * Rendering surface for the admin-authored agreement, kept in step with the
 * authoring one (`.editor-content-preview` in VisualContentEditor, plus the base
 * font/line-height set inline on its contenteditable div). Two reasons this has to
 * exist rather than leaning on a utility class:
 *
 *  - Tailwind's preflight strips `ul`/`ol` down to `list-style: none` with no
 *    padding or margin. The editor undoes that for its own surface; the learner
 *    modal did not, so every bullet and number the admin saw disappeared here.
 *  - The modal previously carried `prose prose-sm`, but @tailwindcss/typography is
 *    not a dependency of this app — those classes match nothing and styled nothing.
 *
 * Deliberately no `!important` and no colour of its own: `<font color>` / `<font
 * face>` / `<font size>` are presentational attributes with zero specificity, so
 * any class on the container (the old `text-gray-800` did exactly this) silently
 * beat the colour the admin picked.
 */
const TERMS_CONTENT_CSS = `
  .terms-html-content {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
    line-height: 1.6;
    overflow-wrap: break-word;
  }
  .terms-html-content ul { list-style-type: disc; padding-left: 2rem; margin: 1rem 0; }
  .terms-html-content ol { list-style-type: decimal; padding-left: 2rem; margin: 1rem 0; }
  .terms-html-content ul ul { list-style-type: circle; }
  .terms-html-content ul ul ul { list-style-type: square; }
  .terms-html-content ol ol { list-style-type: lower-alpha; }
  .terms-html-content ol ol ol { list-style-type: lower-roman; }
  .terms-html-content li { margin: 0.25rem 0; display: list-item; }
`;

/**
 * Blocking Terms & Conditions agreement shown to a learner after they finish all
 * content. The course only completes (and the certificate issues) once they read
 * the document and confirm agreement.
 */
export default function TermsAgreementModal({ isOpen, condition, accepting, onClose, onAccept }: Props) {
  const [agreed, setAgreed] = useState(false);

  if (!condition) return null;

  const title = condition.title || 'Terms & Conditions';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Please read and agree to finish this course"
      size="xl"
      closeOnOverlayClick={false}
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agreed}
              disabled={accepting}
              onChange={(e) => setAgreed(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-brand-indigo-600 focus:ring-brand-indigo-500"
            />
            <span className="text-sm text-gray-700">I have read and agree to the terms above</span>
          </label>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              disabled={accepting}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!agreed || accepting}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-brand-indigo-600 rounded-lg hover:bg-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {accepting && <Loader2 className="w-4 h-4 animate-spin" />}
              Agree &amp; Complete
            </button>
          </div>
        </div>
      }
    >
      <div className="min-h-[300px]">
        {condition.source === 'pdf' && condition.document_url ? (
          // Rendered the same way a lesson PDF is: NON-sandboxed so Chrome's PDF
          // viewer works (it is disabled inside sandboxed frames), with the
          // viewer chrome hidden and the page fitted to width.
          <div className="h-[60vh] rounded-lg border border-gray-200 overflow-hidden bg-white">
            <iframe
              src={`${condition.document_url.split('#')[0]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title={title}
              className="w-full h-full border-none bg-white"
              allow="fullscreen"
              allowFullScreen
            />
          </div>
        ) : condition.html_content ? (
          <>
            <style dangerouslySetInnerHTML={{ __html: TERMS_CONTENT_CSS }} />
            <div
              className="terms-html-content max-w-none"
              dangerouslySetInnerHTML={{ __html: condition.html_content }}
            />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <FileText className="w-8 h-8 mb-2" />
            <p className="text-sm">The terms document is unavailable right now.</p>
          </div>
        )}
      </div>
    </Modal>
  );
}
